import clsx from "clsx";
import {
  ArrowDownAz,
  Calendar,
  EllipsisVertical,
  ListOrdered,
  LucideIcon,
  Merge,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { OrganizationRole } from "~/api/model";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { trackEvent } from "~/utils/analytics";
import { CategoryItem } from "../../types";

interface CategoryActionsProps {
  className?: string;
  handleOpenChange: (isOpen: boolean) => void;
  handleRename: () => void;
  handleDelete: () => void;
  handleMergeAll: () => void;
  categories: CategoryItem[];
  handleSortByName: () => void;
  handleSortByDate: () => void;
  handleSortByPageCount: () => void;
  canBeSorted: boolean;
  /**
   * Handler for the admin-only "New Category" action that runs the
   * currently-grouped pages through the `define-category` prompt and
   * persists the resulting org-level category definition. When undefined,
   * the item is hidden entirely.
   */
  handleDefineCategory?: () => void;
  isDefiningCategory?: boolean;
  /**
   * Handler for the admin-only "Redefine Category" action that refines
   * the existing org-level definition based on the current pages. Only
   * shown when the category is already linked to a DocumentCategory with
   * a non-empty definition.
   */
  handleRedefineCategory?: () => void;
  isRedefiningCategory?: boolean;
}

interface ActionItem {
  id: string;
  icon: LucideIcon;
  messageId: string;
  defaultMessage: string;
  className?: string;
  action: () => void;
  disabled?: boolean;
}

const ADMIN_ROLES = [OrganizationRole.SUPERADMIN, OrganizationRole.OWNER];

export const CategoryActions: FC<CategoryActionsProps> = ({
  className = "",
  handleOpenChange,
  handleRename,
  handleDelete,
  handleMergeAll,
  categories,
  handleSortByName,
  handleSortByDate,
  handleSortByPageCount,
  canBeSorted,
  handleDefineCategory,
  isDefiningCategory = false,
  handleRedefineCategory,
  isRedefiningCategory = false,
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);

  const topItems: ActionItem[] = [
    {
      id: "editName",
      icon: Pencil,
      messageId: "docList.category.actionsDropdown.rename",
      defaultMessage: "Rename",
      action: handleRename,
    },
    canBeSorted && {
      id: "sortByName",
      icon: ArrowDownAz,
      messageId: "docList.category.actionsDropdown.sortByName",
      defaultMessage: "Sort by Name",
      action: handleSortByName,
    },
    canBeSorted && {
      id: "sortByDate",
      icon: Calendar,
      messageId: "docList.category.actionsDropdown.sortByDate",
      defaultMessage: "Sort by Date",
      action: handleSortByDate,
    },
    canBeSorted && {
      id: "sortByPageCount",
      icon: ListOrdered,
      messageId: "docList.category.actionsDropdown.sortByPageCount",
      defaultMessage: "Sort by Page Count",
      action: handleSortByPageCount,
    },
    canBeSorted && {
      id: "mergeAll",
      icon: Merge,
      messageId: "docList.category.actionsDropdown.mergeAll",
      defaultMessage: "Merge All",
      action: handleMergeAll,
    },
  ].filter(Boolean) as ActionItem[];

  const deleteItem: ActionItem | null =
    categories.length > 1
      ? {
          id: "delete",
          icon: Trash2,
          messageId: "docList.category.actionsDropdown.delete",
          defaultMessage: "Delete",
          action: handleDelete,
        }
      : null;

  useEffect(() => {
    if (open) {
      trackEvent({ eventName: "app/doc_list/category_actions.button/click" });
    }
  }, [open]);

  const renderItem = (item: ActionItem) => (
    <DropdownMenuItem
      key={item.id}
      className={clsx("cursor-pointer whitespace-nowrap", item.className)}
      onClick={item.action}
      disabled={item.disabled}
      destructive={item.id === "delete"}
    >
      <item.icon className="mr-2 h-4 w-4" />
      <FormattedMessage
        id={item.messageId}
        defaultMessage={item.defaultMessage}
      />
    </DropdownMenuItem>
  );

  const defineCategoryItem: ActionItem | null = handleDefineCategory
    ? {
        id: "defineCategory",
        icon: Plus,
        messageId: "docList.category.actionsDropdown.defineCategory",
        defaultMessage: "Add Category",
        action: () => {
          trackEvent({
            eventName:
              "app/doc_list/category_actions_menu/define_category.link/click",
          });
          handleDefineCategory();
        },
        disabled: isDefiningCategory,
      }
    : null;

  const redefineCategoryItem: ActionItem | null = handleRedefineCategory
    ? {
        id: "redefineCategory",
        icon: RefreshCw,
        messageId: "docList.category.actionsDropdown.redefineCategory",
        defaultMessage: "Refine Category",
        action: () => {
          trackEvent({
            eventName:
              "app/doc_list/category_actions_menu/redefine_category.link/click",
          });
          handleRedefineCategory();
        },
        disabled: isRedefiningCategory,
      }
    : null;

  return (
    <div onClick={(e) => e.stopPropagation()} className="ml-auto mr-6">
      <DropdownMenu
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          handleOpenChange(isOpen);
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={clsx(
              "h-6 w-6 text-white hover:bg-white/10 hover:text-white",
              className
            )}
            aria-label={intl.formatMessage({ id: "common.moreActions" })}
          >
            <EllipsisVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-34" align="end">
          {topItems.map(renderItem)}
          {(defineCategoryItem || redefineCategoryItem) && (
            <DisplayFor roles={ADMIN_ROLES}>
              <DropdownMenuSeparator />
              {defineCategoryItem && renderItem(defineCategoryItem)}
              {redefineCategoryItem && renderItem(redefineCategoryItem)}
            </DisplayFor>
          )}
          {deleteItem && (
            <>
              <DropdownMenuSeparator />
              {renderItem(deleteItem)}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
